import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {client} from "../../services/api-client";
import {AxiosError} from "axios";
import {IContact, ISocialMedia} from "../../types/generalTypes";
import {toast} from "react-toastify";


export interface IUpdateClientPayload {
    payload: {
        id: number,
        socialMedia: ISocialMedia,
        contact: IContact
    },
    closeForm: () => void
}

export interface ICreateClientPayload {
    payload: {
        name: string
        socialMedia: ISocialMedia,
        contact: IContact
    },
    closeForm: () => void
}

export interface IClient {
    id: number,
    name: string,
    socialMedia: null | ISocialMedia,
    contact: null | IContact
}

interface IGetIClientsResp {
    totalItems: number,
    pageNum: number,
    pageSize: number,
    message: string,
    isOK: boolean,
    sort: null | string,
    sortField: null | string,
    search: null | string,
    items: IClient[]
}

interface IClientsState {
    clients: IClient[],
    totalCount: number,
    pageNumber: number,
    itemsPerPage: number,
    pageSize: number
}

const initialState: IClientsState = {
    clients: [],
    totalCount: 0,
    pageNumber: 1,
    itemsPerPage: 25,
    pageSize: 0
};


export const getClients = createAsyncThunk<IGetIClientsResp, string, { rejectValue: string }>(
    'clients/get-clients',
    async (queryParams, {rejectWithValue}) => {
        try {
            const {data} = await client.get(`api/crm/clients${queryParams}`);
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                return rejectWithValue(e.response?.data)
            }
        }
    }
)

export const createClient = createAsyncThunk<IClient, ICreateClientPayload, { rejectValue: string }>(
    'clients/create-client',
    async (body, {rejectWithValue}) => {
        try {
            const {data} = await client.post(`api/crm/clients`, body.payload);
            toast.info("SDC: Client has been created")
            body.closeForm();
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                return rejectWithValue(e.response?.data)
            }
        }
    }
)

export const updateClient = createAsyncThunk<IClient, IUpdateClientPayload, { rejectValue: string }>(
    'clients/update-client',
    async (body, {rejectWithValue}) => {
        try {
            const {data} = await client.put(`api/crm/clients`, body.payload);
            toast.info("SDC: Client has been updated")
            body.closeForm();
            return data
        } catch (e) {
            if (e instanceof AxiosError) {
                return rejectWithValue(e.response?.data)
            }
        }
    }
)


const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        setPageNumber: (state, action: PayloadAction<number>) => {
            state.pageNumber = action.payload;
        },
        setItemsPerPage: (state, action: PayloadAction<number>) => {
            state.itemsPerPage = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getClients.fulfilled, (state, {payload}) => {
                state.clients = payload.items;
                state.pageNumber = payload.pageNum;
                state.totalCount = payload.totalItems;
                state.pageSize = payload.pageSize
            })
            .addCase(updateClient.fulfilled, (state, action) => {
                state.clients = state.clients.map(client => client.id === action.payload.id ? action.payload : client)
            })
            .addCase(createClient.fulfilled, (state, action) => {
                state.clients.unshift(action.payload)
            })
    }
});

export const {setPageNumber, setItemsPerPage} = clientsSlice.actions;
export default clientsSlice.reducer;
